import React, { useEffect, useRef } from 'react';
import { branding } from '../../constants/branding';

interface SumsubKYCProps {
  applicantId: string;
  accessToken: string;
  onComplete: (status: 'approved' | 'rejected' | 'pending') => void;
  onError: (error: string) => void;
  onClose: () => void;
  enableTokenRefresh?: boolean; // New prop to control token refresh behavior
}

declare global {
  interface Window {
    SNWebSDK: any;
    snsWebSdk: any;
  }
}

const SumsubKYC: React.FC<SumsubKYCProps> = ({
  applicantId,
  accessToken,
  onComplete,
  onError,
  onClose,
  enableTokenRefresh = false // Default to false for registration flow
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sdkRef = useRef<any>(null);

  useEffect(() => {
    // Only initialize if we have both applicantId and accessToken
    if (!applicantId || !accessToken) {
      return;
    }

    // Check if SDK is already loaded (WebSDK 2.0 uses snsWebSdk)
    if (window.snsWebSdk) {
      console.log('Sumsub SDK already loaded, initializing immediately');
      initializeKYC().catch(error => {
        console.error('Failed to initialize KYC:', error);
        onError('Failed to initialize KYC verification');
      });
      return;
    }

    // Load Sumsub WebSDK 2.0
    const script = document.createElement('script');
    script.src = 'https://static.sumsub.com/idensic/static/sns-websdk-builder.js';
    script.async = true;
    script.onload = () => {
      console.log('Sumsub SDK script loaded, waiting for SDK to be available...');
      // Retry mechanism to wait for SDK to be fully initialized
      let retryCount = 0;
      const maxRetries = 20; // 2 seconds total
      
      const checkSDK = () => {
        // Debug: Check what's available on window
        console.log('Checking window object for Sumsub SDK...');
        console.log('window.snsWebSdk:', window.snsWebSdk);
        console.log('window.SNWebSDK:', window.SNWebSDK);
        console.log('Available Sumsub-related properties:', Object.keys(window).filter(key => key.toLowerCase().includes('sumsub') || key.toLowerCase().includes('sn')));
        
        if (window.snsWebSdk) {
          console.log('Sumsub WebSDK 2.0 is now available, initializing...');
          initializeKYC().catch(error => {
            console.error('Failed to initialize KYC:', error);
            onError('Failed to initialize KYC verification');
          });
        } else if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Waiting for Sumsub SDK... attempt ${retryCount}/${maxRetries}`);
          setTimeout(checkSDK, 100);
        } else {
          console.error('Sumsub SDK still not available after maximum retries');
          console.error('Final window object check:', {
            snsWebSdk: window.snsWebSdk,
            SNWebSDK: window.SNWebSDK,
            allKeys: Object.keys(window).filter(key => key.toLowerCase().includes('sumsub') || key.toLowerCase().includes('sn'))
          });
          onError('Failed to load KYC SDK');
        }
      };
      
      checkSDK();
    };
    script.onerror = (error) => {
      console.error('Failed to load Sumsub SDK script:', error);
      console.error('Script src:', script.src);
      onError('Failed to load KYC SDK');
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [applicantId, accessToken]);

  const initializeKYC = async () => {
    console.log('Initializing KYC with:', { applicantId, accessToken: accessToken ? '***' + accessToken.slice(-8) : 'null' });
    console.log('Full access token structure:', accessToken ? accessToken.split('.') : 'null');
    
    if (!containerRef.current || !window.snsWebSdk) {
      console.error('Missing requirements:', { 
        container: !!containerRef.current, 
        sdk: !!window.snsWebSdk 
      });
      onError('Failed to load KYC SDK');
      return;
    }

    try {
      console.log('Initializing Sumsub WebSDK 2.0 with access token...');
      
      // Use the access token directly - it already has userId and levelName embedded
      // No need to call websdkInit endpoint
      
      // Initialize Sumsub WebSDK 2.0 with the access token
      sdkRef.current = window.snsWebSdk
        .init(accessToken, async () => {
          // Token expired callback - refresh the access token
          console.log('KYC token expired, refreshing token...');
          try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
            const response = await fetch(`${apiUrl}/kyc/refresh-token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'text/plain',
              },
              body: applicantId
            });
            
            if (response.ok) {
              const data = await response.json();
              console.log('Token refreshed:', data);
              if (data.status === 'success' && data.data?.accessToken) {
                console.log('Token refreshed successfully');
                return data.data.accessToken;
              }
            }
            
            console.error('Failed to refresh token');
            onError('Failed to refresh KYC token');
            return accessToken; // Fallback to the original token
          } catch (error) {
            console.error('Error refreshing token:', error);
            onError('Error refreshing KYC token');
            return accessToken; // Fallback to the original token
          }
        })
        .withConf({
          lang: 'en'
        })
        .withOptions({ 
          addViewportTag: false,
          adaptIframeHeight: true,
          uiConf: {
            customCssStr: `
              .sumsub-websdk-container {
                height: 100% !important;
                min-height: 500px !important;
              }
              .sumsub-websdk-container iframe {
                height: 100% !important;
                min-height: 500px !important;
              }
              .sumsub-websdk-container .sumsub-websdk-modal {
                height: 100% !important;
                min-height: 500px !important;
              }
            `
          }
        })
        .on('idCheck.onStepCompleted', (payload: any) => {
          console.log('onStepCompleted', payload);
        })
        .on('idCheck.onApproved', (payload: any) => {
          console.log('onApproved', payload);
          onComplete('approved');
        })
        .on('idCheck.onRejected', (payload: any) => {
          console.log('onRejected', payload);
          onComplete('rejected');
        })
        .on('idCheck.onReview', (payload: any) => {
          console.log('onReview', payload);
          onComplete('pending');
        })
        .on('idCheck.onError', (error: any) => {
          console.error('Sumsub error:', error);
          // Check if it's a camera permission error
          if (error.message && error.message.toLowerCase().includes('camera')) {
            console.error('Camera permission error detected');
            onError('Camera access denied. Please allow camera permissions and try again.');
          } else {
            onError(error.message || 'KYC verification failed');
          }
        })
        .build();

      console.log('Launching KYC flow...');
      // Launch KYC flow
      sdkRef.current.launch(containerRef.current);
    } catch (error) {
      console.error('Failed to initialize KYC:', error);
      onError('Failed to initialize KYC verification');
    }
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (sdkRef.current) {
        try {
          // Try to destroy the SDK instance if the method exists
          if (typeof sdkRef.current.destroy === 'function') {
            sdkRef.current.destroy();
          }
        } catch (error) {
          console.warn('Error destroying SDK on unmount:', error);
        }
      }
    };
  }, []);

  const handleClose = () => {
    if (sdkRef.current) {
      // Sumsub WebSDK 2.0 doesn't have a close() method
      // The SDK will handle cleanup automatically
      try {
        // Try to destroy the SDK instance if the method exists
        if (typeof sdkRef.current.destroy === 'function') {
          sdkRef.current.destroy();
        }
      } catch (error) {
        console.warn('Error destroying SDK:', error);
      }
    }
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      fontFamily: branding.fontFamily
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        width: '90%',
        maxWidth: 800,
        height: '90%',
        minHeight: '700px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            margin: 0,
            color: branding.textColor,
            fontSize: 20,
            fontWeight: 600
          }}>
            Identity Verification
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666',
              padding: 0,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* KYC Container */}
        <div 
          ref={containerRef}
          style={{
            flex: 1,
            width: '100%',
            height: '100%'
          }}
        />

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center',
          fontSize: 14,
          color: '#666'
        }}>
          <p style={{ margin: 0 }}>
            Please complete the identity verification process to continue with your registration.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SumsubKYC; 